"use strict";
/**
 * APITable <https://github.com/apitable/apitable>
 * Copyright (C) 2022 APITable Ltd. <https://apitable.com>
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.ResourceService = exports.remoteActions2Operation = void 0;
const core_1 = require("@apitable/core");
const core_2 = require("../core");
const localforage_1 = __importDefault(require("localforage"));
const socket_io_client_1 = __importDefault(require("socket.io-client"));
const store2_1 = __importDefault(require("store2"));
const databus_1 = require("./databus");
const data_storage_provider_1 = require("./databus/data_storage_provider");
const RECONNECT_DELAY = 2000;
const clientWatchedEvents = [
    core_2.OPEventNameEnums.CellUpdated,
    core_2.OPEventNameEnums.RecordCreated,
    core_2.OPEventNameEnums.RecordCommentUpdated,
    core_2.OPEventNameEnums.RecordMetaUpdated,
    core_2.OPEventNameEnums.RecordDeleted,
    core_2.OPEventNameEnums.RecordUpdated,
    core_2.OPEventNameEnums.FieldUpdated,
];
// Apply remote sync over op.
const remoteActions2Operation = (actions) => {
    // The new changes after the transform are applied by a special operation.
    return { cmd: 'REMOTE_NEW_CHANGES', actions };
};
exports.remoteActions2Operation = remoteActions2Operation;
class ResourceService {
    constructor(store, onError) {
        this.store = store;
        this.onError = onError;
        this.reportSocketError = true;
        this.roomIOClear = true;
        this.firstRoomInit = true;
        // The operations generated by the application command, that is, the locally generated op.
        // If a command operates on data from multiple datasheet (associated fields), this will be called multiple times
        this.repairInConsitentcy = (resourceOpsCollects) => {
            const state = this.store.getState();
            console.log('------resourceOpsCollects----', resourceOpsCollects);
            const linkCellMap = this.buildLinkCellMapFromChangeSets(resourceOpsCollects);
            for (const [key, linkAction] of linkCellMap) {
                //oi-od ||only oi ||only od
                const { oi = [], od = [] } = linkAction;
                // 新增的元素
                const added = oi.filter((item) => !od.includes(item));
                console.log('Added:', added); // 输出: ['d', 'e']
                // 要删除的元素
                const removed = od.filter((item) => !oi.includes(item));
                console.log('Removed:', removed); // 输出: ['a']
                const changes = [];
                added.forEach((recordId) => changes.push({ recordId, change: 'added' }));
                removed.forEach((recordId) => changes.push({ recordId, change: 'removed' }));
                if (key.endsWith('1')) { //主记录,以主记录为主，主包含副的，但副的没有主，要增加，反正要删掉，在增量的前提下才行
                    const keyArr = key.split('_');
                    const mainRecordId = keyArr[1];
                    const currentField = core_2.Selectors.getField(state, keyArr[2], keyArr[0]);
                    const foreignDstId = currentField.property.foreignDatasheetId;
                    const brotherFieldId = currentField.property.brotherFieldId;
                    for (const item of changes) {
                        const foreignRecordId = item.recordId;
                        const change = item.change;
                        const foreinKey = this.buildDstFieldIdKey(foreignDstId, foreignRecordId, brotherFieldId, false);
                        const foreinLinkAction = linkCellMap.get(foreinKey);
                        if (foreinLinkAction) {
                            if (change == 'added') {
                                foreinLinkAction.oi = this.changeArray(foreinLinkAction.oi, mainRecordId, true);
                            }
                            else {
                                if (foreinLinkAction.oi) {
                                    foreinLinkAction.oi = this.changeArray(foreinLinkAction.oi, mainRecordId, false);
                                }
                                if (foreinLinkAction.od) {
                                    foreinLinkAction.od = this.changeArray(foreinLinkAction.od, mainRecordId, true);
                                }
                            }
                        }
                        else { //上级都没有咋弄，用引擎来补吧,怎么补，先拿到值，再补
                            const snapshot = core_2.Selectors.getSnapshot(state, foreignDstId);
                            const foreignVals = (core_2.Selectors.getCellValue(state, snapshot, foreignRecordId, brotherFieldId, undefined, undefined, true)) || [];
                            let newVals = [];
                            if (change == 'added') {
                                newVals = this.changeArray(foreignVals, mainRecordId, true);
                            }
                            else {
                                newVals = this.changeArray(foreignVals, mainRecordId, false);
                            }
                            const action = core_1.DatasheetActions.setRecord2Action(snapshot, { recordId: foreignRecordId, fieldId: brotherFieldId, value: newVals });
                            let resourceOpsCollect = resourceOpsCollects.find(resourceOpsCollect => resourceOpsCollect.resourceId == foreignDstId);
                            if (!resourceOpsCollect) {
                                resourceOpsCollect = { resourceId: foreignDstId, resourceType: core_2.ResourceType.Datasheet, operations: [{ cmd: 'SetRecords', actions: [action], mainLinkDstId: keyArr[0] }] };
                                resourceOpsCollects.push(resourceOpsCollect);
                            }
                            else {
                                resourceOpsCollect.operations[0].actions.push(action);
                            }
                        }
                    }
                }
                else { //副记录,以主记录为主，副包含主的，但主的没有副，副的要删除，在增量的前提下才行
                    const keyArr = key.split('_');
                    const foreignRecordId = keyArr[1];
                    const currentField = core_2.Selectors.getField(state, keyArr[2], keyArr[0]);
                    const mainDstId = currentField.property.foreignDatasheetId;
                    const brotherFieldId = currentField.property.brotherFieldId;
                    for (const item of changes) {
                        const mainRecordId = item.recordId;
                        const change = item.change;
                        const mainKey = this.buildDstFieldIdKey(mainDstId, mainRecordId, brotherFieldId, true);
                        const mainAction = linkCellMap.get(mainKey);
                        if (change == 'added') {
                            if ((!mainAction) || (!mainAction.oi) || (mainAction.oi && !mainAction.oi.includes(foreignRecordId))) {
                                if (linkAction.oi) { //你想抱大腿，但主子不干，那也不行啊
                                    linkAction.oi = this.changeArray(linkAction.oi, mainRecordId, false);
                                }
                            }
                        }
                        else {
                            if ((!mainAction) || (!mainAction.od) || (mainAction.od && !mainAction.od.includes(foreignRecordId))) {
                                if (linkAction.od) { //你想和主子脱钩，但主子不干，你也别弄了
                                    linkAction.od = this.changeArray(linkAction.od, mainRecordId, false);
                                }
                            }
                        }
                    }
                }
            }
        };
        this.buildLinkCellMapFromChangeSets = (resourceOpsCollects) => {
            const linkValMap = new Map();
            resourceOpsCollects.forEach(resourceOpsCollect => {
                const { resourceId, operations } = resourceOpsCollect;
                for (const operation of operations) {
                    for (const action of operation.actions) {
                        if (action.p.length === 4 && action.p[0] === 'recordMap' && action.p[2] === 'data') { //更新
                            const recordId = action.p[1];
                            const fieldId = action.p[3];
                            //oi-od only oi only od
                            const currentField = core_2.Selectors.getField(this.store.getState(), fieldId, resourceId);
                            if (currentField && currentField.type == core_2.FieldType.Link) { //有加，有减，都不能错了
                                linkValMap.set(this.buildDstFieldIdKey(resourceId, recordId, fieldId, operation.isMainDst), action);
                            }
                        }
                        // else if (action.n == 'OD' && action.p.length === 2 && action.p[0] === 'recordMap') {//删除,可以暂不处理，顶多来个数据未找到，新纪录插入不涉及关联
                        //   const recordId = action.p[1] as string;
                        //   for (let [fieldId] of action.od.data.entries()) {
                        //     if(fieldTypeMap[fieldId]==FieldType.Link){
                        //       linkValMap.set(this.buildDstFieldIdKey(resourceId,recordId,fieldId,mainLinkDstId),action);
                        //     }
                        //   }
                        // }
                    }
                }
            });
            return linkValMap;
        };
        // The operations generated by the application command, that is, the locally generated op.
        // If a command operates on data from multiple datasheet (associated fields), this will be called multiple times
        this.localOperationDispatch = (resourceOpsCollects) => {
            resourceOpsCollects.forEach(resourceOpsCollect => {
                const { resourceId } = resourceOpsCollect;
                const collaEngine = this.getCollaEngine(resourceId);
                if (!collaEngine) {
                    throw new Error((0, core_2.t)(core_2.Strings.error_not_initialized_datasheet_instance));
                }
            });
            this.applyOperations(this.store, resourceOpsCollects);
        };
        this.operationExecuted = (resourceOpsCollects) => {
            //check and repair inconsistency
            this.repairInConsitentcy(resourceOpsCollects);
            this.localOperationDispatch(resourceOpsCollects);
            // Collaboration of operations to remote.
            this.roomService.syncOperations(resourceOpsCollects);
            // Send opEvent the op of all datasheets generated by this command.
            // this.opEventManager.handleOperations(resourceOpsCollects
            //   .filter(v => v.resourceType === ResourceType.Datasheet) // Only the datasheet related op needs
            //   .map(v => ({ resourceId: v.resourceId, resourceType: v.resourceType, operations: [v.operation] })), this.store);
        };
        this.beforeUnload = (event) => {
            // To prevent the page from closing when there is still user data that has not been sent to the server.
            if (this.roomService && !this.roomService.isSafeToClose()) {
                if (event) {
                    event.returnValue = 'o/';
                }
                return 'o/';
            }
            return;
        };
        this.opEventManager = new core_2.OPEventManager({
            options: {
                enableVirtualEvent: true,
                // enableCombEvent: true, // The client does not need to enable aggregated events.
            },
            // eslint-disable-next-line require-await
            getState: () => {
                return store.getState();
            },
            op2Event: new core_2.OP2Event(clientWatchedEvents),
        });
        this.computeRefManager = new core_2.ComputeRefManager();
        this.databus = this.createDataBus();
        this.commandManager = this.createCommandManager();
    }
    init() {
        if (this.initialized) {
            console.error('Do not repeat the initialization of the datasheet store.');
            return;
        }
        this.initialized = true;
        this.bindBeforeUnload();
        this.socket = this.createSocket();
        this.database = this.createDatabase();
        this.resourceStashManager = new core_2.ResourceStashManager(this.store, () => {
            return this.roomService;
        });
    }
    destroy() {
        if (!this.initialized) {
            return;
        }
        this.initialized = false;
        this.roomService &&
            Array.from(this.roomService.collaEngineMap.values()).forEach(engine => {
                this.reset(engine.resourceId);
            });
        this.socket && this.socket.removeAllListeners();
        this.socket && this.socket.close();
        this.unBindBeforeUnload();
        this.store.dispatch(core_2.StoreActions.setConnected(false));
        this.resourceStashManager.destroy();
    }
    static getResourceFetchAction(resourceType) {
        switch (resourceType) {
            case core_2.ResourceType.Datasheet: {
                return core_2.StoreActions.fetchDatasheet;
            }
            case core_2.ResourceType.Dashboard: {
                return core_2.StoreActions.fetchDashboardPack;
            }
            case core_2.ResourceType.Form: {
                return core_2.StoreActions.fetchForm;
            }
            case core_2.ResourceType.Mirror: {
                return core_2.StoreActions.fetchMirrorPack;
            }
            default: {
                // TODO: Error Handling.
                return core_2.StoreActions.fetchDatasheet;
            }
        }
    }
    switchResource(params) {
        return __awaiter(this, void 0, void 0, function* () {
            console.log('enter datasheet', params);
            const { to, resourceType, extra } = params;
            const allowSwitchRoom = this.allowSwitchRoom();
            allowSwitchRoom && (yield this.switchRoom(to));
            yield this.fetchResource(to, resourceType, false, extra);
            this.createUndoManager(to);
            allowSwitchRoom && (yield this.roomService.init(this.firstRoomInit));
            this.firstRoomInit = false;
        });
    }
    fetchResource(to, resourceType, overWrite = false, extra) {
        switch (resourceType) {
            case core_2.ResourceType.Datasheet:
                return new Promise((resolve, reject) => {
                    this.store.dispatch((0, databus_1.loadDatasheet)(this.database, to, datasheet => {
                        this.currentResource = datasheet;
                        resolve();
                    }, overWrite, extra, () => reject()));
                });
            // TODO dashboard, form, mirror
            default:
                return new Promise((resolve, reject) => {
                    const fetchAction = ResourceService.getResourceFetchAction(resourceType);
                    this.store.dispatch(fetchAction(to, () => {
                        this.currentResource = undefined;
                        resolve();
                    }, overWrite, extra, () => reject()));
                });
        }
    }
    applyOperations(store, resourceOpsCollects) {
        const changesets = resourceOpsCollects;
        const events = this.opEventManager.handleChangesets(changesets);
        this.opEventManager.handleEvents(events, true);
        changesets.forEach(changeset => {
            const { resourceType, operations, resourceId } = changeset;
            console.log('================= apply jot start ================');
            store.dispatch(core_2.StoreActions.applyJOTOperations(operations, resourceType, resourceId));
        });
        // To widget synchronization operations.
        this.opEventManager.handleEvents(events, false);
    }
    /**
     * @description Simply handle the creation and destruction of rooms, and the creation of undoManager.
     * The point of this method is that it is the same as opening a datasheet,
     * but the concept of destroying/creating a room cannot exist in the "template", so this method is more pure,
     * only call when the concept of room is met.
     * @param {string} to
     * @returns {Promise<void>}
     * @private
     */
    switchRoom(to) {
        return __awaiter(this, void 0, void 0, function* () {
            /**
             * Special handling for form, from form to datasheet, there may not be room.
             */
            const collaEngineMap = this.roomService ? yield this.roomService.leaveRoom() : undefined;
            // Create a new room.
            this.roomService = new core_2.RoomService(core_2.RoomService.createRoomId(to), this.socket, () => this.socket.connected, this.store, collaEngineMap, {
                onError: (error, type = 'modal') => this.onError(error, type),
                destroy: () => this.destroy(),
                getRoomIOClear: () => {
                    return this.roomIOClear;
                },
                setRoomIOClear: (status) => {
                    return (this.roomIOClear = status);
                },
                getRoomLastSendTime: () => {
                    return this.roomLastSendTime;
                },
                setRoomLastSendTime: () => {
                    this.roomLastSendTime = Date.now();
                },
            }, this.fetchResource, store2_1.default, localforage_1.default, Number.MAX_VALUE, false);
        });
    }
    /**
     * @description undoManager is bound to the main resource that created the room,
     * and when switching the resource, it is necessary to instantiate undoManager,
     * and bind undoManager to commandManager.
     * @param {string} resourceId
     */
    createUndoManager(resourceId) {
        const undoManager = this.resourceStashManager.getUndoManager(resourceId);
        this.undoManager = undoManager;
        undoManager.setCommandManager(this.commandManager);
        this.commandManager.addUndoStack = (cmd, result, executeType) => {
            const collaEngine = this.getCollaEngine(result.resourceId);
            if (!collaEngine) {
                throw new Error((0, core_2.t)(core_2.Strings.error_not_initialized_datasheet_instance));
            }
            undoManager.addUndoStack({ cmd, result }, executeType);
        };
        if (this.currentResource) {
            this.currentResource.commandManager.addUndoStack = this.commandManager.addUndoStack;
        }
    }
    /**
     * @description Determine if the current operation is taking place "in the template",
     * no resource in the template needs to create/destroy the room.
     */
    allowSwitchRoom() {
        const state = this.store.getState();
        return !state.pageParams.templateId;
    }
    createSocket() {
        const state = this.store.getState();
        const spaceId = state.space.activeId || state.share.spaceId;
        const version = window['__initialization_data__'].version;
        const socket = (0, socket_io_client_1.default)(core_1.Url.WEBSOCKET_NAMESPACE, {
            path: core_1.Url.ROOM_PATH,
            transports: ['websocket'],
            secure: true,
            query: {
                version,
                spaceId,
            },
        });
        socket.on('disconnect', (reason) => {
            console.warn('! ' + 'socket disconnect from server');
            // Player.doTrigger(Events.app_error_logger, { error: new Error('socket disconnect reason: ' + reason) });
            this.store.dispatch(core_2.StoreActions.setReconnecting(true));
            this.roomService.setConnected(false);
            let count = 1;
            const interval = window.setInterval(() => __awaiter(this, void 0, void 0, function* () {
                if (socket.connected) {
                    clearInterval(interval);
                    yield this.roomService.watch();
                    this.roomService.setConnected(true);
                    this.store.dispatch(core_2.StoreActions.setReconnecting(false));
                    console.log('connected');
                    return;
                }
                if (reason === 'io server disconnect') {
                    // the disconnection was initiated by the server, you need to reconnect manually
                    socket.connect();
                    console.warn('! ' + `room attempt to reconnect ${count++} ...`);
                }
            }), RECONNECT_DELAY);
        });
        socket.on('connect', () => {
            this.reportSocketError = true;
            this.store.dispatch(core_2.StoreActions.setConnected(true));
        });
        socket.on('connect_error', () => {
            if (!this.reportSocketError) {
                return;
            }
            this.reportSocketError = false;
            // Player.doTrigger(Events.app_error_logger, { error: new Error('socket has happened some error: ' + JSON.stringify(error)) });
        });
        return socket;
    }
    getCollaEngine(resourceId) {
        return this.roomService.collaEngineMap.get(resourceId);
    }
    changeArray(arr, opElement, isAdd) {
        const newArr = [];
        if (!arr)
            arr = [];
        arr.forEach((element) => {
            if (!isAdd) {
                if (element != opElement)
                    newArr.push(element);
            }
            else {
                newArr.push(element);
            }
        });
        if (isAdd) {
            if (!arr.includes(opElement))
                newArr.push(opElement);
        }
        return newArr;
    }
    buildDstFieldIdKey(dstId, recordId, fieldId, isMainDst) {
        return dstId + '_' + recordId + '_' + fieldId + '_' + (isMainDst ? '1' : '0');
    }
    createDataBus() {
        return core_1.databus.DataBus.create({
            dataStorageProvider: new data_storage_provider_1.ClientDataStorageProvider({
                operationExecuted: (resourceOpsCollects) => this.operationExecuted(resourceOpsCollects),
            }),
            storeProvider: new databus_1.ClientStoreProvider(this.store),
        });
    }
    createDatabase() {
        const database = this.databus.getDatabase();
        database.addEventHandler({
            type: 1 /* databus.event.ResourceEventType.CommandExecuted */,
            handle: event => {
                var _a;
                if (event.execResult === 1 /* databus.event.CommandExecutionResultType.Error */) {
                    const { error, errorType: type } = event;
                    (_a = this.onError) === null || _a === void 0 ? void 0 : _a.call(this, error, type || 'message');
                }
            },
        });
        return database;
    }
    createCommandManager() {
        return new core_2.CollaCommandManager({
            handleCommandExecuted: this.operationExecuted,
            handleCommandExecuteError: (error, type) => {
                var _a;
                (_a = this.onError) === null || _a === void 0 ? void 0 : _a.call(this, error, type || 'message');
            },
        }, this.store);
    }
    bindBeforeUnload() {
        window.addEventListener('beforeunload', this.beforeUnload);
    }
    unBindBeforeUnload() {
        window.removeEventListener('beforeunload', this.beforeUnload);
    }
    /**
     * @description 1. delete the resource data on the store;
     * 2. delete the resource from collaEngineMap provided that the engine has issued an op
     * @param {string} resourceId
     */
    reset(resourceId) {
        this.roomService.quit(resourceId);
    }
    onNewChanges(resourceType, resourceId, actions) {
        console.log('================= change from remote ================');
        return this.applyOperations(this.store, [
            {
                resourceType,
                resourceId,
                operations: [(0, exports.remoteActions2Operation)(actions)],
            },
        ]);
    }
    /**
     * @description Create a collaborative engine (collaEngine) for each resource.
     * @param {string} resourceId
     * @param {ResourceType} resourceType
     * @returns
     */
    createCollaEngine(resourceId, resourceType) {
        const collaEngineMap = this.roomService.collaEngineMap;
        if (collaEngineMap.has(resourceId)) {
            return false;
        }
        const engine = new core_2.Engine({
            resourceId,
            resourceType,
            getRevision: () => core_2.Selectors.getResourceRevision(this.store.getState(), resourceId, resourceType),
            getNetworking: () => core_2.Selectors.getResourceNetworking(this.store.getState(), resourceId, resourceType),
            dispatch: this.store.dispatch,
            getState: this.store.getState,
            lsStore: store2_1.default,
            event: {
                onError: (error) => {
                    var _a;
                    (_a = this.onError) === null || _a === void 0 ? void 0 : _a.call(this, error, 'modal');
                },
                onNewChanges: this.onNewChanges.bind(this),
                onAcceptSystemOperations: (ops) => this.applyOperations(this.store, [
                    {
                        resourceType,
                        resourceId,
                        operations: ops,
                    },
                ]),
                getUndoManager: () => {
                    return this.undoManager;
                },
                reloadResourceData: () => {
                    return this.fetchResource(resourceId, resourceType, true);
                },
            },
        });
        this.roomService.addCollaEngine(engine);
        return true;
    }
    getCollaEngineKeys() {
        return this.roomService.collaEngineMap.keys();
    }
    checkRoomExist() {
        return Boolean(this.roomService);
    }
}
exports.ResourceService = ResourceService;
